import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import VideoPreview from './VideoPreview';
import { RecordedVideo } from '../hooks/useScreenCapture';

interface VideoSortableListProps {
  videos: RecordedVideo[];
  onVideosReorder: (videos: RecordedVideo[]) => void;
  onVideoDelete: (videoId: string) => void;
}

interface SortableVideoItemProps {
  video: RecordedVideo;
  onDelete: (videoId: string) => void;
}

const SortableVideoItem: React.FC<SortableVideoItemProps> = ({ video, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <VideoPreview
        video={video}
        onDelete={onDelete}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

const VideoSortableList: React.FC<VideoSortableListProps> = ({
  videos,
  onVideosReorder,
  onVideoDelete
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = videos.findIndex((video) => video.id === active.id);
      const newIndex = videos.findIndex((video) => video.id === over?.id);

      const reorderedVideos = arrayMove(videos, oldIndex, newIndex);
      onVideosReorder(reorderedVideos);
    }
  };

  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
          <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No videos recorded</h3>
        <p className="text-gray-600">Start recording to see your videos here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Recorded Videos ({videos.length})
        </h3>
        <p className="text-sm text-gray-500">
          Drag and drop to reorder videos
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={videos.map(video => video.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {videos.map((video) => (
              <SortableVideoItem
                key={video.id}
                video={video}
                onDelete={onVideoDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default VideoSortableList;
